/*
 * doc-builder / app.js — страница конструктора документов: мастер → поля → лист
 * → PDF / Word / печать. Один движок на пять страниц (/consent-pd,
 * /power-of-attorney, /loan-receipt, /car-sale-contract, /work-act); что
 * спрашивать на каждой — pages.js, тексты документов — docs/*.js.
 *
 * Данные людей (паспорта, адреса) — третьих лиц, поэтому:
 *   - работа живёт только в этом браузере (__toolDraft.editor, «Мои документы»);
 *   - в телеметрию идут сценарий, число полей и формат файла — ни одного значения;
 *   - лист помечен fs-ignore-rage-clicks: тройной щелчок по абзацу (выделение)
 *     иначе отправил бы 40 символов его текста как «яростный клик»;
 *   - передача сторон в соседний документ — через sessionStorage этой вкладки,
 *     не через адрес (адрес попадает в журналы и Referer).
 */
import { PAGES, applyWizard, wizardValue, isStop, fieldPath, get } from './pages.js';
import * as V from './validate.js';
import { renderDoc, renderKit } from './render-html.js';
import { docxKit } from './render-docx.js';
import { docText, missingFields, fingerprint, who, clean } from './blocks.js';
import { parseMoney, moneyPhrase, kopToRub } from './money.js';
import { nameCases } from './fio.js';
import { totals as actTotals } from './docs/act.js';
import { encodeSpec } from '../stamp-maker/share.js';

const CFG = window.__DB || {};
const PAGE = CFG.page;
const P = PAGES[PAGE];
const I = CFG.i18n || {};
const SLUG = PAGE;
const HANDOFF = 'db:handoff';
// своё у страницы, которому не место в общем приборе (таблица пунктов и текст договора
// у протокола разногласий) — модуль P.ext с крючками: types, onClick/onInput/onPaste/onFocusOut,
// boot, afterRender, next. У пяти прежних страниц его нет — прибор работает как раньше.
const EXT = (P && P.ext) || null;

const $ = id => document.getElementById(id);
const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const tr = (path, vars) => {
    let v = path.split('.').reduce((a, k) => (a == null ? a : a[k]), I);
    if (v == null) return '';
    v = String(v);
    if (vars) v = v.replace(/:(\w+)/g, (m, k) => (k in vars ? String(vars[k]) : m));
    return v;
};
const L = k => tr('f.' + k) || k;
const H = k => tr('h.' + k);
const O = k => tr('o.' + k) || String(k);

function set(o, path, v) {
    const parts = path.split('.');
    let x = o;
    for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        if (x[k] == null || typeof x[k] !== 'object') x[k] = /^\d+$/.test(parts[i + 1]) ? [] : {};
        x = x[k];
    }
    x[parts[parts.length - 1]] = v;
}
const today = (() => { const d = V.parseDate(CFG.today) || (() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate() }; })(); return d; })();

/* ── состояние ──────────────────────────────────────────────────────── */

function freshState() {
    const st = { v: 1, page: PAGE, place: '', date: V.iso(today), parties: {} };
    const sc = P.wizard[0];
    st[P.ns] = P.mod.defaults(P.doc === 'consent' ? sc : undefined);
    if (P.doc === 'act') {
        st.parties.exec = { kind: 'ip' };
        st.parties.cust = { kind: 'ooo' };
    }
    if (P.doc === 'consent') st.parties.operator = { kind: 'ooo' };
    if (P.doc === 'car') { st.carDeal.who = 'self'; }
    if (P.mod.freshParties) Object.assign(st.parties, P.mod.freshParties());
    applyWizard(PAGE, st, sc);
    return st;
}

let S = freshState();
let started = false;
const ui = { mode: 'fill', notes: false, tab: 0, pick: null, foot: true };
const footText = () => (ui.foot ? tr('ui.foot', { date: CFG.checkedText || '21.10.2026' }) : '');
let docs = [];
let drafts = null;

/* Что видит P.ext: состояние, перевод, перерисовка, переход к полю, телеметрия без значений. */
const api = {
    get S() { return S; },
    set(path, v) { set(S, path, v); },
    get: path => get(S, path),
    tr, esc, cfg: CFG, page: PAGE,
    get today() { return V.iso(today); },
    touch: counted => touch(counted),
    rebuild() { buildForm(); render(); },
    render: () => render(),
    schedule: () => scheduleRender(),
    paintWizard: () => paintWizard(),
    focusKey: path => focusKey(path),
    stage: (n, m) => stage(n, m),
    fu: (a, d) => fu(a, d),
    toast: (m, t) => toast(m, t),
    wizardPick: v => wizardPick(v),
    get docs() { return docs; },
};

/* ── мастер ─────────────────────────────────────────────────────────── */

function wizardPick(v, how) {
    const was = wizardValue(PAGE, S);
    applyWizard(PAGE, S, v);
    if (P.doc === 'consent' && v !== was) {
        // у каждого случая свои цели и данные — старые к новому случаю не подходят
        const d = P.mod.defaults(v);
        S.consent.purposes = d.purposes; S.consent.data = d.data; S.consent.pubAllow = d.pubAllow;
    }
    if (P.doc === 'act') { S.parties.exec = S.parties.exec || {}; S.parties.exec.kind = v; if (v === 'npd' || v === 'person') S.act.vatMode = 'none'; }
    if (P.mod.onWizard) P.mod.onWizard(S, v, was);
    paintWizard();
    buildForm();
    render();
    if (how !== 'restore') {
        touch(true);
        // kind — что ответил мастер (ok · notary · info · stop): сколько людей уходит к нотариусу
        const vd = document.querySelector(`#db-wiz [data-verdict="${CSS.escape(String(v))}"]`);
        stage('wizard', { sc: v, kind: vd ? (vd.className.match(/\b(ok|notary|info|stop)\b/) || [])[1] || '' : '' });
    }
}

function paintWizard() {
    const v = wizardValue(PAGE, S);
    document.querySelectorAll('#db-wiz .db-opt').forEach(b => {
        const on = b.dataset.v === v;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    document.querySelectorAll('#db-wiz [data-verdict]').forEach(el => { el.hidden = el.dataset.verdict !== v; });
    const stop = isStop(PAGE, v);
    $('db').classList.toggle('is-stop', stop);
    ['db-form-panel', 'db-out', 'db-desk', 'db-answer'].forEach(id => { const el = $(id); if (el) el.hidden = stop; });
}

/* ── форма ──────────────────────────────────────────────────────────── */

let fieldSeq = 0;
const fid = () => 'dbf' + (++fieldSeq);

function hintHtml(h) { return h ? `<span class="tc-hint db-h">${esc(h)}</span>` : ''; }

function inputHtml(f, val) {
    const id = fid();
    const lab = L(f.l);
    const req = typeof f.req === 'function' ? f.req(S) : f.req;
    const star = req ? '<span class="db-req" aria-hidden="true">*</span>' : '';
    const sfx = f.suffix ? `<span class="db-sfx">${esc(L(f.suffix))}</span>` : '';
    const im = f.inputmode ? ` inputmode="${f.inputmode}"` : '';
    // автозаполнение браузера — только там, где у поля есть стандартное имя (ФИО, адрес,
    // телефон, почта), и отдельной секцией на каждую сторону: «мои данные» встают в одну
    // сторону договора, а не во все сразу. Паспорт и реквизиты — off.
    const ac = ` autocomplete="${f.ac || 'off'}"`;
    // «Далее» на клавиатуре телефона: Enter переводит к следующему полю (wire → onEnter)
    const ek = ' enterkeyhint="next"';
    const hint = f.h ? hintHtml(H(f.h)) : '';
    const msg = `<span class="db-msg" data-msg-for="${esc(f.k)}" role="status"></span>`;
    switch (f.type) {
        case 'textarea':
            return `<label class="tc-f db-f"><span class="tc-f-l">${esc(lab)}${star}</span><textarea class="tc-in" id="${id}" data-k="${esc(f.k)}" rows="2"${ac}>${esc(val)}</textarea>${hint}${msg}</label>`;
        case 'date':
            return `<label class="tc-f db-f db-f-date"><span class="tc-f-l">${esc(lab)}${star}</span><input class="tc-in" id="${id}" type="date" data-k="${esc(f.k)}" value="${esc(val)}">${hint}${msg}</label>`;
        case 'money':
            return `<label class="tc-f db-f"><span class="tc-f-l">${esc(lab)}${star}</span><span class="db-sfxw"><input class="tc-in" id="${id}" data-k="${esc(f.k)}" data-type="money" inputmode="decimal" autocomplete="off"${ek} value="${esc(val)}"><span class="db-sfx">₽</span></span><span class="db-words" data-words-for="${esc(f.k)}"></span>${hint}${msg}</label>`;
        case 'check':
            return `<label class="db-check"><input type="checkbox" id="${id}" data-k="${esc(f.k)}"${val ? ' checked' : ''}><span>${esc(lab)}${f.h ? `<span class="tc-hint db-h">${esc(H(f.h))}</span>` : ''}</span></label>`;
        case 'seg': {
            const keys = f.optKeys || f.opts.map(o => f.l + '_' + o);
            const btns = f.opts.map((o, i) => `<button type="button" class="tc-seg-b${String(val) === String(o) ? ' on' : ''}" data-k="${esc(f.k)}" data-v="${esc(JSON.stringify(o))}" aria-pressed="${String(val) === String(o)}">${esc(O(keys[i]))}</button>`).join('');
            return `<div class="tc-f db-f"><span class="tc-f-l">${esc(lab)}</span><div class="tc-seg db-seg" role="group" aria-label="${esc(lab)}">${btns}</div>${hint}</div>`;
        }
        case 'select': {
            const keys = f.optKeys || f.opts.map(o => f.l + '_' + o);
            const os = f.opts.map((o, i) => `<option value="${esc(o)}"${String(val) === String(o) ? ' selected' : ''}>${esc(O(keys[i]))}</option>`).join('');
            return `<label class="tc-f db-f"><span class="tc-f-l">${esc(lab)}</span><select class="tc-in" id="${id}" data-k="${esc(f.k)}">${os}</select>${hint}</label>`;
        }
        default:
            return `<label class="tc-f db-f${f.short ? ' db-short' : ''}"><span class="tc-f-l">${esc(lab)}${star}</span><span class="db-sfxw"><input class="tc-in" id="${id}" data-k="${esc(f.k)}"${im}${ac}${ek} value="${esc(val)}">${sfx}</span>${hint}${msg}</label>`;
    }
}

function personHtml(f) {
    const k = 'parties.' + f.key;
    const p = get(S, k) || {};
    const t = (name, l, o) => inputHtml(Object.assign({ k: k + '.' + name, type: 'text', l }, o || {}), get(p, name) || '');
    let h = '<div class="db-person">';
    const sec = 'section-' + f.key + ' ';
    h += `<div class="db-fio">${t('fio', 'fio', { req: true, h: 'fio', ac: sec + 'name' })}<div class="db-cases" data-cases-for="${esc(f.key)}"></div></div>`;
    h += '<div class="tc-fields db-grid">';
    if (!f.noBirth) h += inputHtml({ k: k + '.birth', type: 'date', l: 'birth', req: true }, p.birth || '');
    h += t('pass', 'pass', { req: true, inputmode: 'numeric', h: 'pass' });
    h += inputHtml({ k: k + '.passDate', type: 'date', l: 'passDate', req: true }, p.passDate || '');
    h += t('passCode', 'passCode', { inputmode: 'numeric', short: true });
    h += '</div>';
    h += t('passBy', 'passBy', { req: true, h: 'passBy' });
    h += inputHtml({ k: k + '.addr', type: 'textarea', l: 'addr', req: true, h: 'addr', ac: sec + 'street-address' }, p.addr || '');
    if (f.contacts) h += '<div class="tc-fields db-grid">' + t('phone', 'phone', { inputmode: 'tel', ac: sec + 'tel' }) + t('email', 'email', { inputmode: 'email', ac: sec + 'email' }) + '</div>';
    h += '</div>';
    return h;
}

function orgHtml(f) {
    const k = 'parties.' + f.key;
    const o = get(S, k) || {};
    const kind = o.kind || f.kinds[0];
    const t = (name, l, x) => inputHtml(Object.assign({ k: k + '.' + name, type: 'text', l }, x || {}), get(o, name) || '');
    let h = '<div class="db-org">';
    h += inputHtml({ k: k + '.kind', type: 'seg', l: 'org_kind', opts: f.kinds, optKeys: f.kinds.map(x => 'kind_' + x) }, kind);
    // f.light — реквизиты необязательны (протокол разногласий: ИНН и ОГРН уже есть в самом договоре)
    const rq = !f.light;
    if (kind === 'ooo') {
        h += t('name', 'org_name', { req: true, h: 'org_name' }) + t('short', 'org_short');
        h += '<div class="tc-fields db-grid">' + t('inn', 'inn10', { req: rq, inputmode: 'numeric' }) + (f.light ? '' : t('kpp', 'kpp', { inputmode: 'numeric' })) + t('ogrn', 'ogrn13', { req: rq, inputmode: 'numeric' }) + '</div>';
        h += '<div class="tc-fields db-grid">' + t('signerPos', 'signerPos', { h: 'signerPos' }) + t('fio', 'signerFio', { h: f.basis ? 'signerFio_doc' : 'signerFio' }) + (f.basis ? t('basis', 'signerBasis', { h: 'signerBasis' }) : '') + '</div>';
    } else if (kind === 'ip') {
        h += t('fio', 'ip_fio', { req: true });
        h += '<div class="tc-fields db-grid">' + t('inn', 'inn12', { req: rq, inputmode: 'numeric' }) + t('ogrn', 'ogrnip', { req: rq, inputmode: 'numeric' }) + '</div>';
    } else if (kind === 'npd') {
        h += t('fio', 'fio', { req: true }) + '<div class="tc-fields db-grid">' + t('inn', 'inn12', { req: rq, inputmode: 'numeric' }) + '</div>';
    } else {
        h += t('fio', 'fio', { req: true });
    }
    if (!f.noAddr) h += inputHtml({ k: k + '.addr', type: 'textarea', l: kind === 'ooo' ? 'org_addr' : 'addr', req: P.doc === 'consent' }, o.addr || '');
    if (f.bank && kind !== 'person') {
        h += `<details class="tc-drawer db-drawer"><summary><span class="tc-drawer-ico" aria-hidden="true">🏦</span>${esc(tr('ui.bank'))}</summary><div class="tc-drawer-body">`;
        h += t('bank', 'bank') + '<div class="tc-fields db-grid">' + t('bik', 'bik', { inputmode: 'numeric' }) + t('acc', 'acc', { inputmode: 'numeric' }) + t('corr', 'corr', { inputmode: 'numeric' }) + '</div></div></details>';
    }
    h += '</div>';
    return h;
}

function carHtml(f) {
    const k = f.ns;
    const c = get(S, k) || {};
    const t = (name, l, x) => inputHtml(Object.assign({ k: k + '.' + name, type: 'text', l }, x || {}), get(c, name) || '');
    let h = '<div class="db-car">';
    h += t('make', 'car_make', { req: true, h: 'car_make' });
    h += '<div class="tc-fields db-grid">' + t('vin', 'vin', { req: true, h: 'vin' }) + t('year', 'car_year', { req: true, inputmode: 'numeric', short: true }) + t('plate', 'plate', { req: !f.lite, h: 'plate' }) + t('color', 'color') + '</div>';
    if (!f.lite) h += '<div class="tc-fields db-grid">' + t('engine', 'engine') + t('body', 'body') + t('chassis', 'chassis') + t('mileage', 'mileage', { inputmode: 'numeric' }) + '</div>';
    h += inputHtml({ k: k + '.ptsKind', type: 'seg', l: 'ptsKind', opts: ['epts', 'paper'], optKeys: ['pts_epts', 'pts_paper'] }, c.ptsKind || 'epts');
    h += '<div class="tc-fields db-grid">' + t('pts', c.ptsKind === 'paper' ? 'pts_paper_no' : 'pts_epts_no', { inputmode: c.ptsKind === 'paper' ? 'text' : 'numeric' });
    if (c.ptsKind === 'paper' && !f.lite) h += t('ptsBy', 'ptsBy') + inputHtml({ k: k + '.ptsDate', type: 'date', l: 'ptsDate' }, c.ptsDate || '');
    h += '</div><div class="tc-fields db-grid">' + t('sts', 'sts', { h: 'sts' });
    if (!f.lite) h += t('stsBy', 'stsBy') + inputHtml({ k: k + '.stsDate', type: 'date', l: 'stsDate' }, c.stsDate || '');
    h += '</div></div>';
    return h;
}

function childHtml(f) {
    const k = f.ns;
    const c = get(S, k) || {};
    const t = (name, l, x) => inputHtml(Object.assign({ k: k + '.' + name, type: 'text', l }, x || {}), get(c, name) || '');
    let h = '<div class="db-child">';
    h += `<div class="db-fio">${t('fio', 'child_fio', { req: true })}<div class="db-cases" data-cases-for="${esc(k)}"></div></div>`;
    h += '<div class="tc-fields db-grid">' + inputHtml({ k: k + '.birth', type: 'date', l: 'child_birth', req: true }, c.birth || '') + '</div>';
    h += inputHtml({ k: k + '.docType', type: 'seg', l: 'child_doc', opts: ['cert', 'passport'], optKeys: ['doc_cert', 'doc_passport'] }, c.docType || 'cert');
    h += '<div class="tc-fields db-grid">' + t('docSeries', 'docSeries', { short: true }) + t('docNumber', 'docNumber', { inputmode: 'numeric', short: true }) + inputHtml({ k: k + '.docDate', type: 'date', l: 'docDate' }, c.docDate || '') + '</div>';
    h += t('docBy', 'docBy', { h: 'docBy' });
    h += '</div>';
    return h;
}

function itemsHtml(f) {
    const items = get(S, f.k) || [];
    const rows = items.map((it, i) => `<div class="db-item" data-i="${i}">
        <input class="tc-in db-it-name" data-k="${f.k}.${i}.name" value="${esc(it.name)}" placeholder="${esc(L('it_name'))}" aria-label="${esc(L('it_name'))}" autocomplete="off">
        <input class="tc-in db-it-unit" data-k="${f.k}.${i}.unit" value="${esc(it.unit)}" aria-label="${esc(L('it_unit'))}" placeholder="${esc(L('it_unit'))}" autocomplete="off">
        <input class="tc-in db-it-qty" data-k="${f.k}.${i}.qty" value="${esc(it.qty)}" inputmode="decimal" aria-label="${esc(L('it_qty'))}" placeholder="${esc(L('it_qty'))}" autocomplete="off">
        <input class="tc-in db-it-price" data-k="${f.k}.${i}.price" data-type="money" value="${esc(it.price)}" inputmode="decimal" aria-label="${esc(L('it_price'))}" placeholder="${esc(L('it_price'))}" autocomplete="off">
        <button type="button" class="db-it-del" data-del="${i}" aria-label="${esc(tr('ui.item_del'))}"${items.length < 2 ? ' disabled' : ''}>×</button></div>`).join('');
    return `<div class="db-items" data-items="${esc(f.k)}"><div class="db-items-head" aria-hidden="true"><span>${esc(L('it_name'))}</span><span>${esc(L('it_unit'))}</span><span>${esc(L('it_qty'))}</span><span>${esc(L('it_price'))}</span><span></span></div>${rows}
        <div class="tc-actions"><button type="button" class="tc-btn sm ghost" data-add-item>＋ ${esc(tr('ui.item_add'))}</button><span class="tc-hint">${esc(tr('ui.item_paste'))}</span></div>
        <p class="db-total" id="db-total"></p></div>`;
}

function multiHtml(f) {
    const chosen = new Set(get(S, f.k) || []);
    const items = f.presets.map((p, i) => `<label class="db-check"><input type="checkbox" data-multi="${esc(f.k)}" data-val="${esc(p)}"${chosen.has(p) ? ' checked' : ''}><span>${esc(p)}</span></label>`).join('');
    const custom = inputHtml({ k: f.custom, type: 'textarea', l: f.l + '_custom', h: 'one_per_line' }, get(S, f.custom) || '');
    return `<div class="tc-f db-f db-multi"><span class="tc-f-l">${esc(L(f.l))}${f.req ? '<span class="db-req" aria-hidden="true">*</span>' : ''}</span>${items}${custom}</div>`;
}

function allowHtml(f) {
    const chosen = new Set(get(S, f.k) || []);
    const items = f.presets.map((p, i) => `<label class="db-check"><input type="checkbox" data-allow="${esc(f.k)}" data-i="${i}"${chosen.has(i) ? ' checked' : ''}><span>${esc(p)}</span></label>`).join('');
    return `<div class="tc-f db-f db-multi"><span class="tc-f-l">${esc(L(f.l))}</span>${items}</div>`;
}

function fieldHtml(f) {
    if (f.show && !f.show(S)) return '';
    if (EXT && EXT.types && EXT.types[f.type]) return EXT.types[f.type](f, api);
    switch (f.type) {
        case 'person': return personHtml(f);
        case 'org': return orgHtml(f);
        case 'car': return carHtml(f);
        case 'child': return childHtml(f);
        case 'items': return itemsHtml(f);
        case 'multi': return multiHtml(f);
        case 'allow': return allowHtml(f);
        default: return inputHtml(f, f.type === 'check' ? !!get(S, f.k) : (get(S, f.k) == null ? '' : get(S, f.k)));
    }
}

const openSections = new Set();

function buildForm() {
    const host = $('db-form');
    if (!host) return;
    const focus = document.activeElement && host.contains(document.activeElement) ? document.activeElement.dataset.k : null;
    fieldSeq = 0;
    const secs = P.form(S);
    let h = `<div class="tc-fields db-grid db-common">${inputHtml({ k: 'place', type: 'text', l: 'place', h: 'place' }, S.place || '')}${inputHtml({ k: 'date', type: 'date', l: 'docdate', req: true }, S.date || '')}</div>`;
    for (const s of secs) {
        const body = s.fields.map(fieldHtml).join('');
        if (!body) continue;
        if (s.drawer || s.title) {
            const open = !s.drawer || openSections.has(s.id) ? ' open' : '';
            h += `<details class="tc-drawer db-sec" data-sec="${esc(s.id)}"${open}><summary>${esc(tr('s.' + (s.title || s.id), s.vars) || s.title)}<span class="db-sec-st" data-sec-st="${esc(s.id)}"></span></summary><div class="tc-drawer-body">${body}</div></details>`;
        } else h += `<div class="db-sec db-sec-flat" data-sec="${esc(s.id)}">${body}</div>`;
    }
    host.innerHTML = h;
    host.querySelectorAll('details.db-sec').forEach(d => d.addEventListener('toggle', () => { if (d.open) openSections.add(d.dataset.sec); else openSections.delete(d.dataset.sec); }));
    if (focus) { const el = host.querySelector(`[data-k="${CSS.escape(focus)}"]`); if (el && el.focus) el.focus({ preventScroll: true }); }
    paintDerived();
    validateAll();
}

/* ── ввод ───────────────────────────────────────────────────────────── */

let renderTimer = 0;
function scheduleRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(() => { renderTimer = 0; render(); }, 140);
}

let formStarted = false;
function onInput(e) {
    const el = e.target;
    if (!formStarted && e.isTrusted !== false) {
        formStarted = true;
        try { window.__aeFunnel && window.__aeFunnel('form_start', { sc: String(wizardValue(PAGE, S)) }); } catch (_) {}
    }
    if (EXT && EXT.onInput && EXT.onInput(e, api)) return;
    if (el.dataset.multi) {
        const k = el.dataset.multi;
        const cur = new Set(get(S, k) || []);
        if (el.checked) cur.add(el.dataset.val); else cur.delete(el.dataset.val);
        // порядок — как в списке, а не как отмечали
        const order = Array.from(document.querySelectorAll(`[data-multi="${CSS.escape(k)}"]`)).map(x => x.dataset.val);
        set(S, k, order.filter(v => cur.has(v)));
        touch(true); scheduleRender(); return;
    }
    if (el.dataset.allow) {
        const k = el.dataset.allow;
        const cur = new Set(get(S, k) || []);
        const i = +el.dataset.i;
        if (el.checked) cur.add(i); else cur.delete(i);
        set(S, k, Array.from(cur).sort((a, b) => a - b));
        touch(true); scheduleRender(); return;
    }
    const k = el.dataset.k;
    if (!k) return;
    // ручная форма падежа — сигнал, что склонение ошиблось (качество FioEngine), без самой формы
    if (/\.fix\.\w+$/.test(k) && !fixSent.has(k.replace(/\.fix\.\w+$/, ''))) { fixSent.add(k.replace(/\.fix\.\w+$/, '')); fu('fio_fix', { kase: k.split('.').pop() }); }
    let v = el.type === 'checkbox' ? el.checked : el.value;
    const prev = get(S, k);
    if (/\.pass$/.test(k)) {
        // «Серия и номер» одним полем → passSeries / passNumber
        const d = V.digitsOnly(v);
        const base = k.slice(0, -5);
        set(S, base + '.passSeries', d.slice(0, 4));
        set(S, base + '.passNumber', d.slice(4, 10));
    }
    set(S, k, v);
    if (P.mod.onSet) P.mod.onSet(S, k, v, prev);
    touch(true);
    if (el.type === 'checkbox' || el.tagName === 'SELECT') { buildForm(); render(); return; }
    paintDerived(k);
    validateOne(k, el);
    scheduleRender();
}

function onClick(e) {
    if (EXT && EXT.onClick && EXT.onClick(e, api)) return;
    const seg = e.target.closest('.db-seg .tc-seg-b[data-k]');
    if (seg) {
        const prev = get(S, seg.dataset.k);
        set(S, seg.dataset.k, JSON.parse(seg.dataset.v));
        if (P.mod.onSet) P.mod.onSet(S, seg.dataset.k, JSON.parse(seg.dataset.v), prev);
        if (seg.dataset.k === 'parties.exec.kind' && P.doc === 'act') {
            const v = JSON.parse(seg.dataset.v);
            if (v === 'npd' || v === 'person') S.act.vatMode = 'none';
            paintWizard();
        }
        touch(true); buildForm(); render(); return;
    }
    const add = e.target.closest('[data-add-item]');
    if (add) { S.act.items.push({ name: '', unit: 'усл.', qty: '1', price: '' }); touch(true); buildForm(); render(); focusKey(`act.items.${S.act.items.length - 1}.name`); fu('item_add', { n: S.act.items.length }); return; }
    const del = e.target.closest('[data-del]');
    if (del) { S.act.items.splice(+del.dataset.del, 1); if (!S.act.items.length) S.act.items.push({ name: '', unit: 'усл.', qty: '1', price: '' }); touch(true); buildForm(); render(); return; }
    const fix = e.target.closest('[data-fix]');
    if (fix) { const box = fix.parentElement.querySelector('.db-fixbox'); if (box) { box.hidden = !box.hidden; fix.setAttribute('aria-expanded', String(!box.hidden)); if (!box.hidden) fu('cases_open'); } }
}

/** Вставка строк из Excel/таблицы в наименование работ: колонки раскладываются по полям. */
function onPaste(e) {
    if (EXT && EXT.onPaste && EXT.onPaste(e, api)) return;
    const el = e.target;
    if (!el.classList || !el.classList.contains('db-it-name')) return;
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text || (!/\t/.test(text) && !/\n./.test(text))) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, '').split('\n').map(l => l.trim()).filter(Boolean);
    const start = +el.closest('.db-item').dataset.i;
    const rows = lines.map(l => {
        const c = l.split('\t').map(x => x.trim());
        if (c.length >= 4) return { name: c[0], unit: c[1] || 'усл.', qty: c[2] || '1', price: c[3] };
        if (c.length === 3) return { name: c[0], unit: 'усл.', qty: c[1] || '1', price: c[2] };
        if (c.length === 2) return { name: c[0], unit: 'усл.', qty: '1', price: c[1] };
        return { name: c[0], unit: 'усл.', qty: '1', price: '' };
    });
    S.act.items.splice(start, 1, ...rows);
    touch(true); buildForm(); render();
    stage('items_paste', { n: rows.length });
}

/* ── производные подсказки: прописью, падежи, итог ─────────────────── */

function paintDerived(changed) {
    document.querySelectorAll('[data-words-for]').forEach(el => {
        const v = get(S, el.dataset.wordsFor);
        const p = parseMoney(v);
        el.textContent = p.ok && p.kop > 0 ? moneyPhrase(p) : '';
    });
    document.querySelectorAll('[data-cases-for]').forEach(el => {
        const key = el.dataset.casesFor;
        if (changed && !changed.startsWith(key.includes('.') ? key : 'parties.' + key)) return;
        const p = key.includes('.') ? get(S, key) : get(S, 'parties.' + key);
        el.innerHTML = casesHtml(p || {}, key.includes('.') ? key : 'parties.' + key);
    });
    const tot = $('db-total');
    if (tot && P.doc === 'act') {
        const T = actTotals(S);
        tot.textContent = T.sum > 0 ? tr('ui.total_line', { sum: kopToRub(T.gross), vat: T.vat ? kopToRub(T.vat) : '' }) + (T.vat ? '' : '') : '';
    }
}

function casesHtml(p, base) {
    const nc = nameCases(p.fio, { gender: p.gender || 'auto', fix: p.fix });
    if (!nc.ok) return '';
    if (!nc.declined) return `<span class="tc-hint">${esc(tr('ui.cases_wait'))}</span>`;
    const gl = nc.gender === 'female' ? tr('ui.g_female') : tr('ui.g_male');
    const rows = [['gent', tr('ui.c_gent')], ['datv', tr('ui.c_datv')], ['accs', tr('ui.c_accs')], ['ablt', tr('ui.c_ablt')]];
    const fixInputs = rows.map(([c, l]) => `<label class="tc-f db-f"><span class="tc-f-l">${esc(l)}</span><input class="tc-in" data-k="${esc(base)}.fix.${c}" value="${esc((p.fix && p.fix[c]) || '')}" placeholder="${esc(nc.cases[c])}" autocomplete="off"></label>`).join('');
    const g = ['auto', 'male', 'female'].map(x => `<button type="button" class="tc-seg-b${(p.gender || 'auto') === x ? ' on' : ''}" data-k="${esc(base)}.gender" data-v='"${x}"' aria-pressed="${(p.gender || 'auto') === x}">${esc(tr('ui.g_' + x))}</button>`).join('');
    return `<span class="db-cases-l">${esc(tr('ui.cases_l'))}</span> ${esc(nc.cases.datv)} · ${esc(nc.cases.gent)} <span class="db-cases-g">(${esc(gl)}${nc.genderSure ? '' : ' ?'})</span>
        <button type="button" class="db-link no-tap-floor" data-fix aria-expanded="false">${esc(tr('ui.cases_fix'))}</button>
        <div class="db-fixbox" hidden><div class="tc-seg db-seg" role="group" aria-label="${esc(tr('ui.gender'))}">${g}</div><div class="tc-fields db-grid">${fixInputs}</div><span class="tc-hint">${esc(tr('ui.cases_fix_h'))}</span></div>`;
}

/* ── проверка полей ─────────────────────────────────────────────────── */

function partyOf(k) { const m = /^parties\.(\w+)\./.exec(k); return m ? m[1] : null; }

function checkKey(k) {
    const v = get(S, k);
    const has = v != null && String(v).trim() !== '';
    const last = k.split('.').pop();
    const pk = partyOf(k);
    const party = pk ? (S.parties[pk] || {}) : null;
    const kind = party && party.kind;
    if (!has) return null;
    switch (last) {
        case 'pass': return V.passport(party.passSeries, party.passNumber, { today, issued: V.parseDate(party.passDate) });
        case 'passCode': return V.divisionCode(v);
        case 'passDate': return V.passportDates(V.parseDate(party.birth), V.parseDate(v), today);
        case 'birth': return V.birthDate(V.parseDate(v), today, { adult: !/^(child)/.test(k) && !k.includes('.child.') });
        case 'inn': return V.inn(v, kind === 'ooo' ? 'org' : (kind === 'ip' || kind === 'npd' ? 'person' : undefined));
        case 'ogrn': return V.ogrn(v, kind === 'ooo' ? 'org' : kind === 'ip' ? 'ip' : undefined);
        case 'kpp': return V.kpp(v);
        case 'bik': return V.bik(v);
        case 'acc': return V.account(v, party && party.bik);
        case 'corr': return V.account(v, party && party.bik, 'corr');
        case 'vin': return V.vin(v);
        case 'year': return k.includes('car') ? V.carYear(v, today) : null;
        case 'plate': return V.plate(v);
        case 'sts': return V.sts(v);
        case 'pts': return get(S, k.replace(/\.pts$/, '.ptsKind')) === 'paper' ? V.ptsPaper(v) : V.eptsNumber(v);
        case 'email': case 'revokeEmail': return V.email(v);
        case 'phone': return V.phone(v);
        default:
            if (/(amount|price|rest|boughtFor)$/.test(last)) { const p = parseMoney(v); return p.ok ? { ok: true, level: 'ok' } : { ok: false, level: 'error', code: 'money_format' }; }
            return null;
    }
}

function validateOne(k, el) {
    const box = document.querySelector(`[data-msg-for="${CSS.escape(k)}"]`);
    if (!box) return;
    const r = checkKey(k);
    const input = el || document.querySelector(`[data-k="${CSS.escape(k)}"]`);
    if (!r || r.level === 'ok') { box.textContent = ''; box.className = 'db-msg'; if (input) input.removeAttribute('aria-invalid'); return; }
    box.textContent = tr('v.' + r.code, Object.assign({}, r.params || {})) || r.code;
    box.className = 'db-msg ' + (r.level === 'error' ? 'bad' : 'warn');
    if (input) { if (r.level === 'error') input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid'); }
}
/**
 * Где люди ошибаются: поле (без стороны и значения) и код проверки — раз на поле и код за страницу.
 * Только по уходу из поля: на каждой клавише недонабранный паспорт «4» слал бы «серия — 4 цифры».
 */
const diagSent = new Set();
function diag(k, r) {
    if (!started || !r || !r.code) return;
    const field = k.split('.').pop();
    const id = field + ':' + r.code;
    if (diagSent.has(id)) return;
    diagSent.add(id);
    try { window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'field_check', field, code: r.code, level: r.level }); } catch (_) {}
}
function validateAll() {
    document.querySelectorAll('[data-msg-for]').forEach(b => validateOne(b.dataset.msgFor));
}

/* ── лист, комплект, ответ ──────────────────────────────────────────── */

function ctx() { return { today, keyRate: CFG.keyRate || null }; }

function fieldLabel(id) {
    // строка таблицы протокола: «п. 2 — предлагаемая редакция», а не пять одинаковых меток
    if (P.mod.missLabel) { const x = P.mod.missLabel(id, S, k => tr('miss.' + k)); if (x) return x; }
    const path = fieldPath(PAGE, id);
    const last = id.split('.').pop();
    const head = id.split('.')[0];
    const role = (P.mod.roleLabel && P.mod.roleLabel(head, S)) || tr('roles.' + head);
    const lab = tr('miss.' + last) || L(last);
    return role ? `${lab} — ${role}` : lab;
}

function render() {
    if (isStop(PAGE, wizardValue(PAGE, S))) return;
    try { docs = P.mod.build(S, ctx()); } catch (err) { docs = []; window.__handleError && window.__handleError(err, 'doc_builder_build', { say: tr('ui.err_build') }); return; }
    if (ui.tab >= docs.length) ui.tab = 0;
    paintTabs();
    paintSheet();
    paintAnswer();
    paintKitPick();
    paintLinks();
    paintNext();
    if (EXT && EXT.afterRender) EXT.afterRender(api, docs);
}

function paintTabs() {
    const host = $('db-tabs');
    if (!host) return;
    host.hidden = docs.length < 2;
    host.innerHTML = docs.map((d, i) => `<button type="button" class="tc-seg-b${i === ui.tab ? ' on' : ''}" role="tab" aria-selected="${i === ui.tab}" data-tab="${i}">${esc(d.title)}${d.copies ? ` <small>×${d.copies.length}</small>` : ''}</button>`).join('');
}

function paintSheet() {
    const sheet = $('db-sheet');
    const d = docs[ui.tab];
    if (!sheet || !d) return;
    // на листе метка короче: роль и так видна из текста вокруг («от … (далее — Займодавец)»)
    sheet.innerHTML = renderDoc(d, { mode: ui.mode, screen: true, notes: ui.notes, foot: footText(), label: id => tr('miss.' + id.split('.').pop()) || L(id.split('.').pop()), whyTitle: tr('ui.why') });
    sheet.classList.toggle('is-blank', ui.mode === 'blank');
    const kit = $('db-kit');
    if (kit) kit.hidden = !(d.kit);
    if (d.kit) paintSiteKit(d.kit);
    const note = $('db-copies-note');
    if (note) { note.hidden = !d.copies; if (d.copies) note.textContent = tr('ui.copies_note', { n: d.copies.length }); }
}

function paintSiteKit(k) {
    const set1 = (id, v) => { const el = $(id); if (el) el.textContent = v; };
    set1('db-kit-checkbox', '☐ ' + k.checkbox);
    set1('db-kit-mailing', k.mailing ? '☐ ' + k.mailing : '');
    const mw = $('db-kit-mailing-row'); if (mw) mw.hidden = !k.mailing;
    set1('db-kit-html', k.html);
}

function allMissing() {
    const ids = new Set();
    for (const d of docs) for (const f of missingFields(d)) ids.add(f);
    return Array.from(ids);
}

let filledSent = false;
function paintAnswer() {
    const v = $('db-answer-v'), sub = $('db-answer-sub'), list = $('db-miss'), wl = $('db-warn');
    if (!v) return;
    const miss = ui.mode === 'blank' ? [] : allMissing();
    if (ui.mode === 'blank') { v.textContent = tr('ui.st_blank'); sub.textContent = tr('ui.st_blank_sub'); }
    else if (!miss.length) {
        v.textContent = tr('ui.st_ready'); sub.textContent = tr('ui.st_ready_sub');
        // шаг воронки: документ впервые заполнен целиком (только если человек правда заполнял)
        if (started && formStarted && !filledSent) { filledSent = true; stage('filled', { sc: String(wizardValue(PAGE, S)), docs: docs.length }); }
    }
    else { v.textContent = tr('ui.st_missing', { n: miss.length }); sub.textContent = tr('ui.st_missing_sub'); }
    $('db-answer').classList.toggle('ok', ui.mode === 'blank' || !miss.length);
    // на широком экране метки стоят в узкой колонке по одной в строке — шесть, остальное «и ещё N»:
    // двенадцать отодвигали лист документа на экран вниз (1440 px, QA 23.10.2026); пустые поля видны и на самом листе
    const cap = ($('db').getBoundingClientRect().width || 0) >= 1080 ? 6 : 12;
    list.innerHTML = miss.slice(0, cap).map(id => `<button type="button" class="db-chip-miss" data-f="${esc(id)}">${esc(fieldLabel(id))}</button>`).join('') + (miss.length > cap ? `<span class="tc-hint">${esc(tr('ui.more', { n: miss.length - cap }))}</span>` : '');
    list.hidden = !miss.length;
    const ws = (P.mod.warnings ? P.mod.warnings(S, ctx()) : []).concat(fieldErrors());
    wl.innerHTML = ws.map(w => {
        const text = tr('warn.' + w.code, w.params) || tr('v.' + w.code, w.params) || w.code;
        return `<li class="db-w ${w.level}">${esc(text)}</li>`;
    }).join('');
    wl.hidden = !ws.length;
}

/** Ошибки формата в полях (ИНН с неверной контрольной суммой…) — и в общем списке, чтобы их не пропустили. */
function fieldErrors() {
    const out = [];
    document.querySelectorAll('[data-msg-for]').forEach(b => {
        if (b.classList.contains('bad')) out.push({ code: 'field_bad', level: 'error', params: { field: (b.closest('.db-f') || b).querySelector('.tc-f-l') ? (b.closest('.db-f').querySelector('.tc-f-l').textContent || '').replace('*', '') : '', msg: b.textContent } });
    });
    return out;
}

function paintKitPick() {
    const host = $('db-pick');
    if (!host) return;
    if (docs.length < 2) { host.hidden = true; ui.pick = null; return; }
    host.hidden = false;
    const ids = docs.map(d => d.id);
    if (!ui.pick || ui.pick.some(x => !ids.includes(x))) ui.pick = ids.filter(id => id !== 'memo' || true);
    host.innerHTML = `<span class="tc-f-l">${esc(tr('ui.in_file'))}</span>` + docs.map(d => `<label class="db-check db-pick-i"><input type="checkbox" data-pick="${esc(d.id)}"${ui.pick.includes(d.id) ? ' checked' : ''}><span>${esc(d.title)}${d.copies ? ' ×' + d.copies.length : ''}</span></label>`).join('');
}

function picked() {
    if (docs.length < 2 || !ui.pick) return docs.slice();
    const out = docs.filter(d => ui.pick.includes(d.id));
    return out.length ? out : docs.slice(0, 1);
}

/* ── связки: соседние документы, печать, подпись, юрист ─────────────── */

function paintLinks() {
    const stamp = $('db-stamp-link');
    if (stamp && P.doc === 'act') {
        const E = S.parties.exec || {};
        stamp.hidden = !(E.kind === 'ip' || E.kind === 'ooo');
        if (!stamp.hidden) stampLink(E).then(href => { if (href) stamp.href = href; });
    }
}

let stampSeq = 0;
async function stampLink(E) {
    const my = ++stampSeq;
    const base = CFG.links && CFG.links.stamp;
    if (!base) return '';
    const nc = who({ fio: E.fio });
    const spec = E.kind === 'ooo'
        ? { preset: 'ru-ooo', shape: 'round', fields: { name_full: clean(E.name), name_short: clean(E.short), ogrn: V.digitsOnly(E.ogrn), inn: V.digitsOnly(E.inn), kpp: clean(E.kpp), city: clean(S.place) } }
        : { preset: 'ru-ip', shape: 'round', fields: { fio: nc.ok ? nc.fio : '', ogrnip: V.digitsOnly(E.ogrn), inn: V.digitsOnly(E.inn), city: clean(S.place) } };
    try {
        const r = await encodeSpec(spec);
        return my === stampSeq ? base + '#d=' + r.str : '';
    } catch (e) { return base; }
}

/** Стороны — в соседний документ этой вкладки (sessionStorage, не адрес). */
function handoff(target) {
    const p = S.parties;
    const map = {
        'power-of-attorney': () => ({ parties: { principal: p.seller || p.principal, agent: p.sellerRep || p.agent }, poa: P.doc === 'car' ? { scenario: 'car', carSell: true, carGibdd: true, car: Object.assign({}, S.carDeal && S.carDeal.car) } : undefined }),
        'loan-receipt': () => ({ parties: { borrower: p.borrower || p.buyer, lender: p.lender || p.seller, payee: p.seller || p.payee, payer: p.buyer || p.payer } }),
        'car-sale-contract': () => ({ parties: { seller: p.principal || p.seller, buyer: p.buyer } }),
        'consent-pd': () => ({ parties: { subject: p.principal || p.subject } }),
        'work-act': () => ({ parties: {} }),
    };
    const pack = (map[target] || (() => ({ parties: {} })))();
    pack.place = S.place;
    pack.at = Date.now();
    pack.from = PAGE;
    try { sessionStorage.setItem(HANDOFF, JSON.stringify(pack)); } catch (e) {}
}

function takeHandoff() {
    let pack = null;
    try { pack = JSON.parse(sessionStorage.getItem(HANDOFF) || 'null'); sessionStorage.removeItem(HANDOFF); } catch (e) {}
    if (!pack || !pack.at || Date.now() - pack.at > 30 * 60 * 1000 || pack.from === PAGE) return false;
    let any = false;
    // сторона с ФИО, а из счёта (/invoice-generator) — и организация с наименованием или ИНН
    for (const [k, v] of Object.entries(pack.parties || {})) if (v && (clean(v.fio) || clean(v.name) || clean(v.inn))) { S.parties[k] = Object.assign({}, v); any = true; }
    if (pack.place) S.place = pack.place;
    if (pack.poa && P.doc === 'poa') { Object.assign(S.poa, pack.poa); any = true; }
    // акт по счёту: позиции, НДС, номер и договор (/invoice-generator handoff.js actPack)
    if (pack.act && P.doc === 'act') {
        const a = pack.act;
        for (const k of ['number', 'vatMode', 'vatRate', 'vatBasis', 'contractName', 'contractNumber', 'contractDate']) if (a[k] != null && a[k] !== '') S.act[k] = String(a[k]);
        if (Array.isArray(a.items) && a.items.length) S.act.items = a.items.map(it => ({ name: clean(it.name), unit: clean(it.unit) || 'усл.', qty: clean(it.qty) || '1', price: clean(it.price) }));
        if (S.parties.exec && (S.parties.exec.kind === 'npd' || S.parties.exec.kind === 'person')) S.act.vatMode = 'none';
        any = true;
    }
    if (any) {
        const n = $('db-handoff');
        if (n) { n.hidden = false; n.textContent = tr('ui.handoff_in', { from: tr('docs.' + pack.from) || pack.from }); }
        stage('handoff_in', { from: pack.from });
    }
    return any;
}

/* ── выдача: PDF, Word, печать, копия ───────────────────────────────── */

function kitText(list, mode) { return list.map(d => (d.copyBlocks || [d.blocks]).map(b => docText({ blocks: b }, mode)).join('\n\n')).join('\n\n----\n\n'); }

/**
 * Отложенная перерисовка (140 мс после ввода) — сейчас: выдача обязана нести то, что человек
 * только что набрал. Без этого Tab → Enter по «Скачать» отдавал документ без последней
 * правки, а та же правка следующим форматом становилась «другим документом» и съедала
 * вторую единицу суточного лимита (QA 23.10.2026).
 */
function flushRender() { if (renderTimer) { clearTimeout(renderTimer); renderTimer = 0; render(); } }

function begin(list, fmt) {
    const key = 'doc:' + fingerprint(kitText(list, ui.mode) + '|' + ui.mode);
    if (!window.__toolLimit) return { commit() {}, abort() {} };
    return window.__toolLimit.begin(SLUG, [key]);
}

function fileBase(list) {
    const d = list[0];
    let name = list.length > 1 ? (tr('ui.kit_name', { doc: d.file }) || d.file) : d.file;
    const party = firstParty();
    if (party && ui.mode !== 'blank') name += ' — ' + party;
    if (ui.mode === 'blank') name += ' (' + tr('ui.blank_suffix') + ')';
    return window.__outputName ? window.__outputName.sanitize(name) || 'document' : name.replace(/[\\/:*?"<>|]+/g, ' ');
}
function firstParty() {
    for (const k of (P.mod.parties ? P.mod.parties(S) : [])) {
        const p = S.parties[k];
        if (p && clean(p.fio)) return nameCases(p.fio).short.replace(/ /g, ' ');
        if (p && p.kind === 'ooo' && clean(p.short || p.name)) return clean(p.short || p.name);
    }
    return '';
}

function busy(btn, on) { if (!btn) return; btn.disabled = !!on; btn.classList.toggle('is-busy', !!on); }

function paperHtml(list, o) {
    return renderKit(list, { mode: ui.mode, screen: false, copies: true, foot: o && o.noFoot ? '' : footText() });
}

async function exportPdf() {
    flushRender();
    const list = picked();
    if (!list.length || !window.HtmlPdf) { toast(tr('ui.err_pdf_lib'), 'error'); return; }
    const run = begin(list, 'pdf');
    if (!run) return;
    const btn = $('db-pdf');
    busy(btn, true);
    const host = document.createElement('div');
    host.className = 'db-paper db-pdfhost';
    host.setAttribute('aria-hidden', 'true');
    host.style.fontFamily = window.HtmlPdf.FAMILY ? window.HtmlPdf.FAMILY.serif : '';
    // строка о дате сверки — в нижнем поле последней страницы, а не абзацем текста: абзацем она
    // уходила одна на лишнюю страницу «2 / 2» (доверенность на авто, согласие на распространение)
    host.innerHTML = paperHtml(list, { noFoot: true });
    document.body.appendChild(host);
    try {
        stage('export', { fmt: 'pdf', docs: list.length, mode: ui.mode, miss: ui.mode === 'blank' ? 0 : allMissing().length });
        const title = list.length > 1 ? tr('ui.kit_name', { doc: list[0].file }) : list[0].file;
        const foot = footText();
        const MMPT = 72 / 25.4;
        const opts = {
            pages: 'auto', page: 'a4', marginsMm: { t: 20, r: 15, b: 20, l: 25 }, title, lang: 'ru', creator: 'Timbrica',
            footer: (n, all) => (all > 1 ? n + ' / ' + all : ''),
            decorate: foot ? (pg => (pg.page === pg.pages ? [{ text: foot, align: 'left', xPt: 25 * MMPT, yPt: 13 * MMPT, sizePt: 8, color: 'rgb(119,119,119)' }] : [])) : null,
        };
        const sections = Array.from(host.querySelectorAll(':scope > .db-doc'));
        // памятка к ДКП строки о сверке не несёт (как и на листе — render-html, doc.memo)
        const optsOf = sec => (sec && sec.dataset.memo ? Object.assign({}, opts, { decorate: null }) : opts);
        let bytes;
        if (sections.length < 2) bytes = await window.HtmlPdf.fromElement(host, optsOf(sections[0]));
        else {
            // Каждый документ комплекта (и каждый экземпляр договора) — своим проходом:
            // нумерация страниц у каждого своя («1 / 2» у экземпляра покупателя, а не «3»).
            // pdf-lib HtmlPdf грузит сам при первом вызове — поэтому сначала куски, потом склейка
            const parts = [];
            for (const sec of sections) parts.push(await window.HtmlPdf.fromElement(sec, optsOf(sec)));
            const L = window.PDFLib;
            const out = await L.PDFDocument.create();
            for (const part of parts) {
                const src = await L.PDFDocument.load(part);
                const pages = await out.copyPages(src, src.getPageIndices());
                pages.forEach(p => out.addPage(p));
            }
            out.setTitle(title); out.setCreator('Timbrica'); out.setProducer('Timbrica HtmlPdf');
            try { out.setLanguage('ru'); } catch (e) {}
            bytes = await out.save();
        }
        const ok = await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), fileBase(list) + '.pdf');
        if (ok === false) { run.abort(); return; }
        run.commit();
        exported('pdf', list);
    } catch (err) {
        run.abort();
        if (window.__isUserCancel && window.__isUserCancel(err)) return;
        window.__handleError ? window.__handleError(err, 'doc_builder_pdf', { say: tr('ui.err_pdf') }) : toast(tr('ui.err_pdf'), 'error');
    } finally {
        host.remove();
        busy(btn, false);
    }
}

async function exportDocx() {
    flushRender();
    const list = picked();
    if (!window.DocxWriter) { toast(tr('ui.err_docx'), 'error'); return; }
    const run = begin(list, 'docx');
    if (!run) return;
    const btn = $('db-docx');
    busy(btn, true);
    try {
        stage('export', { fmt: 'docx', docs: list.length, mode: ui.mode });
        const blob = await window.DocxWriter.build({
            docTitle: list[0].file, font: 'Times New Roman', size: 24, line: 264, lang: 'ru-RU',
            blocks: docxKit(list, ui.mode, { copies: true, foot: footText() }),
        });
        const ok = await window.__downloadFile(blob, fileBase(list) + '.docx');
        if (ok === false) { run.abort(); return; }
        run.commit();
        exported('docx', list);
    } catch (err) {
        run.abort();
        window.__handleError ? window.__handleError(err, 'doc_builder_docx', { say: tr('ui.err_docx') }) : toast(tr('ui.err_docx'), 'error');
    } finally { busy(btn, false); }
}

function printKit() {
    flushRender();
    const list = picked();
    const run = begin(list, 'print');
    if (!run) return;
    const host = $('db-print');
    host.innerHTML = paperHtml(list);
    stage('export', { fmt: 'print', docs: list.length, mode: ui.mode });
    const done = () => { window.removeEventListener('afterprint', done); host.innerHTML = ''; };
    window.addEventListener('afterprint', done);
    try { window.print(); run.commit(); exported('print', list); } catch (err) { run.abort(); }
}

async function copyText() {
    flushRender();
    const d = docs[ui.tab];
    if (!d) return;
    const run = begin([d], 'copy');
    if (!run) return;
    const text = (d.kit ? [d.kit.page.join('\n\n')] : [docText(d, ui.mode)]).join('');
    try {
        await navigator.clipboard.writeText(text);
        run.commit();
        toast(tr('ui.copied'), 'success');
        exported('copy', [d]);
    } catch (e) { run.abort(); toast(tr('ui.err_copy'), 'error'); }
}

async function copyOf(id) {
    const el = $(id);
    if (!el) return;
    try { await navigator.clipboard.writeText(el.textContent.replace(/^☐ /, '')); toast(tr('ui.copied'), 'success'); } catch (e) { toast(tr('ui.err_copy'), 'error'); }
}

function exported(fmt, list) {
    if (drafts) drafts.exported();
    if (window.__aeToolComplete) window.__aeToolComplete({ action: fmt, docs: list.length, mode: ui.mode, sc: String(wizardValue(PAGE, S)) });
}

/* ── следующий документ из этого ─────────────────────────────────────── */

/**
 * Что человек делает следующим шагом с тем же документом — и что раньше набирал заново:
 *   return  → расписка о возврате этого же долга (стороны, сумма, дата займа переходят сами);
 *   act     → следующий акт тому же заказчику (номер +1, сегодня, период на месяц вперёд);
 *   person  → то же согласие другому человеку (оператор, цели и данные остаются).
 * Прежний документ остаётся в «Моих документах» (drafts.leave), страница становится новым.
 */
function nextKinds() {
    if (P.mod.nextKinds) return P.mod.nextKinds(S);
    if (P.doc === 'receipt' && S.receipt.kind === 'loan') return ['return'];
    if (P.doc === 'act') return CFG.links && CFG.links.invoice ? ['act', 'invoice'] : ['act'];
    if (P.doc === 'consent' && S.consent.scenario !== 'site') return ['person'];
    return [];
}
function paintNext() {
    const host = $('db-next');
    if (!host) return;
    const kinds = nextKinds();
    host.hidden = !kinds.length;
    const html = kinds.map(k => `<li><button type="button" class="db-link db-next-b" data-next="${k}">${esc(tr('ui.next_' + k))}</button> <span class="tc-hint">${esc(tr('ui.next_' + k + '_h'))}</span></li>`).join('');
    if (host.innerHTML !== html) host.innerHTML = html;
}
function addMonth(isoDate, lastDay) {
    const d = V.parseDate(isoDate);
    if (!d) return '';
    const y = d.m === 12 ? d.y + 1 : d.y, m = d.m === 12 ? 1 : d.m + 1;
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return V.iso({ y, m, d: lastDay ? last : Math.min(d.d, last) });
}
/**
 * Счёт по этому акту — в /invoice-generator (sessionStorage 'ig:handoff', тот же вид, что
 * читает invoice/app.js takeHandoff): стороны с реквизитами, работы, НДС, номер и договор.
 * Акт остаётся здесь как был; уходит только копия данных.
 */
function invoicePack() {
    const p = S.parties || {}, a = S.act || {};
    const E = p.exec || {}, Cu = p.cust || {};
    const fio = x => (x && clean(x.fio)) || '';
    const seller = { kind: E.kind || 'ip', inn: V.digitsOnly(E.inn), kpp: clean(E.kpp), ogrn: V.digitsOnly(E.ogrn), addr: clean(E.addr), acc: V.digitsOnly(E.acc), bank: clean(E.bank), bik: V.digitsOnly(E.bik), corr: V.digitsOnly(E.corr), headFio: fio(E), headPos: clean(E.signerPos) };
    seller.name = E.kind === 'ooo' ? clean(E.short || E.name) : E.kind === 'ip' ? (fio(E) ? 'ИП ' + fio(E) : '') : fio(E);
    const buyer = { kind: Cu.kind === 'ooo' ? 'org' : Cu.kind === 'ip' ? 'ip' : 'person', inn: V.digitsOnly(Cu.inn), kpp: clean(Cu.kpp), addr: clean(Cu.addr) };
    buyer.name = Cu.kind === 'ooo' ? clean(Cu.short || Cu.name) : Cu.kind === 'ip' ? (fio(Cu) ? 'ИП ' + fio(Cu) : '') : fio(Cu);
    const items = (a.items || []).filter(it => clean(it.name) || clean(it.price)).map(it => ({ kind: 'item', name: clean(it.name), qty: clean(it.qty) || '1', unit: clean(it.unit), price: clean(it.price), disc: '', vat: '' }));
    const state = {
        form: 'ru', type: 'invoice', number: clean(a.number) || '1', date: V.iso(today), seller, buyer,
        tax: { mode: a.vatMode === 'in' || a.vatMode === 'top' ? a.vatMode : 'none', rate: +a.vatRate || 22, basis: { usn: 'usn', psn: 'psn' }[a.vatBasis] || '', perLine: false, name: '' },
        basis: clean(a.contractNumber) || clean(a.contractDate) ? { kind: 'contract', number: clean(a.contractNumber), date: clean(a.contractDate), text: '' } : { kind: 'none', number: '', date: '', text: '' },
    };
    if (items.length) state.items = items;
    return { from: PAGE, fromTitle: tr('docs.' + PAGE), at: Date.now(), state };
}

function nextDoc(kind) {
    if (kind === 'invoice') {
        try { sessionStorage.setItem('ig:handoff', JSON.stringify(invoicePack())); } catch (e) {}
        stage('next', { kind });
        location.href = CFG.links.invoice;
        return;
    }
    if (drafts) drafts.leave();
    const prevDate = S.date;
    if (kind === 'return') {
        S.receipt.kind = 'return';
        S.receipt.origDoc = 'receipt';
        S.receipt.origDate = prevDate || '';
        S.receipt.full = true;
        S.receipt.original = 'returned';
        S.date = V.iso(today);
        paintWizard();
    } else if (kind === 'act') {
        const a = S.act;
        const n = String(a.number || '').trim();
        if (/^\d+$/.test(n)) a.number = String(+n + 1);
        else if (/\d+$/.test(n)) a.number = n.replace(/(\d+)$/, x => String(+x + 1).padStart(x.length, '0'));
        const to = V.parseDate(a.periodTo);
        const lastTo = !!to && to.d === new Date(Date.UTC(to.y, to.m, 0)).getUTCDate();
        if (a.periodFrom && a.periodTo) { a.periodFrom = addMonth(a.periodFrom, false); a.periodTo = addMonth(a.periodTo, lastTo); }
        a.result = 'ok'; a.remarks = ''; a.fixUntil = ''; a.checkNo = ''; a.checkDate = '';
        S.date = V.iso(today);
    } else if (EXT && EXT.next && EXT.next(kind, api)) {
        // своё «следующее» у страницы (протокол разногласий → протокол согласования)
    } else if (kind === 'person') {
        S.parties.subject = {};
        if (S.consent.child) S.consent.child = { fio: '', gender: 'auto', birth: '', docType: 'cert', docSeries: '', docNumber: '', docBy: '', docDate: '' };
        S.date = V.iso(today);
    }
    touch(true);
    buildForm();
    render();
    toast(tr('ui.next_done'), 'success');
    stage('next', { kind });
    // поле, с которого продолжать: сумма возврата, первая строка работ, ФИО нового человека
    focusKey(EXT && EXT.nextFocus ? EXT.nextFocus(kind, api) : kind === 'return' ? 'receipt.amount' : kind === 'act' ? 'act.items.0.name' : (S.consent.scenario === 'child' ? 'consent.child.fio' : 'parties.subject.fio'));
}

/* ── прочее ─────────────────────────────────────────────────────────── */

/** feature_use: что нажимали — без значений полей (данные третьих лиц). */
function fu(action, data) { try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, action }, data || {})); } catch (_) {} }
const fixSent = new Set();

/**
 * «Далее» на клавиатуре телефона и Enter на компьютере: к следующему полю формы.
 * В многострочном адресе Enter переносит строку, как обычно; последнее поле
 * убирает клавиатуру. Страницу не прокручиваем — фокус сам держит поле на экране.
 */
function onEnter(e) {
    if (e.key !== 'Enter' || e.isComposing || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    const el = e.target;
    if (!el || el.tagName !== 'INPUT' || el.type === 'checkbox' || el.type === 'radio') return;
    e.preventDefault();
    const all = Array.from(document.querySelectorAll('#db-form input:not([type=checkbox]):not([type=radio]):not([type=hidden]), #db-form textarea, #db-form select'))
        .filter(x => !x.disabled && x.offsetParent !== null && !x.closest('[hidden]'));
    const i = all.indexOf(el);
    const nx = i >= 0 ? all[i + 1] : null;
    if (nx) nx.focus(); else el.blur();
}

function toast(m, t) { if (window.__toast) window.__toast(m, t || 'info'); }
function stage(name, meta) { try { if (window.__stage) window.__stage(name, meta || {}); } catch (e) {} }
function touch(counted) { started = true; if (drafts) drafts.touch(counted); }

function focusKey(path) {
    if (!path) return;
    let el = document.querySelector(`#db-form [data-k="${CSS.escape(path)}"]`);
    if (!el && /\.(passSeries|passNumber)$/.test(path)) el = document.querySelector(`#db-form [data-k="${CSS.escape(path.replace(/\.(passSeries|passNumber)$/, '.pass'))}"]`);
    if (!el) {
        // поле в закрытом разделе или скрыто веткой — раскроем раздел и попробуем ещё раз
        document.querySelectorAll('#db-form details.db-sec:not([open])').forEach(d => { d.open = true; });
        el = document.querySelector(`#db-form [data-k="${CSS.escape(path)}"]`);
    }
    if (!el) return;
    const sec = el.closest('details');
    if (sec && !sec.open) sec.open = true;
    el.scrollIntoView({ block: 'nearest' });
    el.focus({ preventScroll: true });
    el.classList.add('db-flash');
    setTimeout(() => el.classList.remove('db-flash'), 1400);
}

function wire() {
    const wiz = $('db-wiz');
    // нажатие до загрузки модуля держит __earlyAction (head), здесь его забираем
    if (window.__earlyAction) window.__earlyAction.claim('db-wiz', a => { if (a) wizardPick(a); });
    wiz.addEventListener('click', e => { const b = e.target.closest('.db-opt'); if (b) wizardPick(b.dataset.v); });
    const form = $('db-form');
    form.addEventListener('input', onInput);
    form.addEventListener('change', e => { if (e.target.type === 'checkbox' || e.target.tagName === 'SELECT' || e.target.type === 'date') onInput(e); });
    form.addEventListener('click', onClick);
    form.addEventListener('paste', onPaste);
    form.addEventListener('keydown', onEnter);
    form.addEventListener('focusout', e => {
        if (EXT && EXT.onFocusOut) EXT.onFocusOut(e, api);
        const k = e.target.dataset && e.target.dataset.k;
        if (!k) return;
        // запись в документ: «4512345678» → «45 12 345678», «770001» → «770-001»
        if (/\.pass$/.test(k)) { const p = S.parties[partyOf(k)] || {}; const r = V.passport(p.passSeries, p.passNumber); if (r.ok) e.target.value = r.value.series + ' ' + r.value.number; }
        if (/\.passCode$/.test(k)) { const r = V.divisionCode(e.target.value); if (r.ok) { e.target.value = r.value; set(S, k, r.value); } }
        if (/\.vin$/.test(k)) { const r = V.vin(e.target.value); if (r.ok) { e.target.value = r.value; set(S, k, r.value); scheduleRender(); } }
        if (/\.plate$/.test(k)) { const r = V.plate(e.target.value); if (r.ok) { e.target.value = r.value; set(S, k, r.value); scheduleRender(); } }
        diag(k, checkKey(k));
        // «9901123456» → «99 01 123456», «77уа123456» → «77 УА 123456» — как в документе
        if (/\.sts$/.test(k)) { const r = V.sts(e.target.value); if (r.ok) { e.target.value = r.value; set(S, k, r.value); scheduleRender(); } }
        if (/\.pts$/.test(k)) { const paper = get(S, k.replace(/\.pts$/, '.ptsKind')) === 'paper'; const r = paper ? V.ptsPaper(e.target.value) : V.eptsNumber(e.target.value); if (r.ok) { e.target.value = r.value; set(S, k, r.value); scheduleRender(); } }
    });
    $('db-tabs').addEventListener('click', e => { const b = e.target.closest('[data-tab]'); if (!b) return; ui.tab = +b.dataset.tab; paintTabs(); paintSheet(); fu('tab', { doc: (docs[ui.tab] || {}).id || '' }); });
    const sheet = $('db-sheet');
    sheet.addEventListener('click', e => {
        const w = e.target.closest('.db-why');
        if (w) { const n = document.getElementById(w.getAttribute('aria-controls')); if (n) { n.hidden = !n.hidden; w.setAttribute('aria-expanded', String(!n.hidden)); if (!n.hidden) fu('why_open'); } return; }
        const f = e.target.closest('[data-f]');
        if (f) { focusKey(fieldPath(PAGE, f.dataset.f)); fu('jump', { from: 'sheet' }); }
    });
    $('db-miss').addEventListener('click', e => { const b = e.target.closest('[data-f]'); if (b) { focusKey(fieldPath(PAGE, b.dataset.f)); fu('jump', { from: 'chip' }); } });
    $('db-pick').addEventListener('change', e => { const c = e.target.closest('[data-pick]'); if (!c) return; const id = c.dataset.pick; ui.pick = ui.pick.filter(x => x !== id); if (c.checked) ui.pick.push(id); ui.pick = docs.map(d => d.id).filter(x => ui.pick.includes(x)); fu('pick', { doc: id, on: c.checked ? 1 : 0 }); });
    document.querySelectorAll('#db-mode .tc-seg-b').forEach(b => b.addEventListener('click', () => setMode(b.dataset.mode, true)));
    const notes = $('db-notes');
    notes.addEventListener('change', () => { ui.notes = notes.checked; paintSheet(); fu('notes', { on: ui.notes ? 1 : 0 }); });
    const foot = $('db-foot');
    if (foot) {
        foot.checked = ui.foot;
        foot.addEventListener('change', () => { ui.foot = foot.checked; try { window.__toolSettings && window.__toolSettings.save(SLUG, 'foot', ui.foot ? 1 : 0); } catch (e) {} paintSheet(); fu('foot', { on: ui.foot ? 1 : 0 }); });
    }
    $('db-pdf').addEventListener('click', exportPdf);
    $('db-docx').addEventListener('click', exportDocx);
    $('db-print-btn').addEventListener('click', printKit);
    $('db-copy').addEventListener('click', copyText);
    document.querySelectorAll('[data-copy-of]').forEach(b => b.addEventListener('click', () => { copyOf(b.dataset.copyOf); fu('kit_copy', { part: b.dataset.copyOf.replace('db-kit-', '') }); }));
    document.querySelectorAll('[data-handoff]').forEach(a => a.addEventListener('click', () => { handoff(a.dataset.handoff); fu('sibling', { to: a.dataset.handoff }); }));
    document.querySelectorAll('[data-db-link]').forEach(a => a.addEventListener('click', () => fu('link', { to: a.dataset.dbLink })));
    const next = $('db-next');
    if (next) next.addEventListener('click', e => { const b = e.target.closest('[data-next]'); if (b) nextDoc(b.dataset.next); });
    const live = $('db-live');
    if (live) live.addEventListener('click', () => { const d = $('db-desk'); if (d) d.scrollIntoView({ block: 'nearest' }); });
}

function setMode(m, user) {
    ui.mode = m === 'blank' ? 'blank' : 'fill';
    document.querySelectorAll('#db-mode .tc-seg-b').forEach(b => { const on = b.dataset.mode === ui.mode; b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on)); });
    if (user) { try { window.__toolSettings && window.__toolSettings.save && window.__toolSettings.save(SLUG, 'mode', ui.mode); } catch (e) {} stage('mode', { mode: ui.mode }); }
    paintSheet(); paintAnswer();
}

/* ── черновики: только в этом браузере ─────────────────────────────── */

function initDrafts() {
    if (!window.__toolDraft || !window.__toolDraft.editor || !$('dbDrafts')) return null;
    return window.__toolDraft.editor({
        slug: SLUG, root: 'dbDrafts', icon: '📄', openKey: 'db-draft-open:' + SLUG,
        payload: () => {
            const nm = firstParty();
            const d = docs[0];
            return { v: 1, state: JSON.parse(JSON.stringify(S)), label: [d ? d.title : '', nm].filter(Boolean).join(' — '), meta: { sc: String(wizardValue(PAGE, S)) } };
        },
        apply: d => {
            const st = d && d.state;
            if (st && st.page === PAGE) {
                S = Object.assign(freshState(), st);
                S[P.ns] = Object.assign(P.mod.defaults(P.doc === 'consent' ? (st.consent && st.consent.scenario) : undefined), st[P.ns] || {});
            }
            paintWizard(); buildForm(); render();
            return Promise.resolve([]);
        },
        fresh: () => { S = freshState(); started = false; paintWizard(); buildForm(); render(); },
        started: () => started,
    });
}

/* ── старт ──────────────────────────────────────────────────────────── */

async function boot() {
    if (!P || !$('db')) return;
    try { const m = window.__toolSettings && window.__toolSettings.load && window.__toolSettings.load(SLUG, 'mode', 'fill'); if (m === 'blank') ui.mode = 'blank'; } catch (e) {}
    try { const f = window.__toolSettings && window.__toolSettings.load && window.__toolSettings.load(SLUG, 'foot', 1); ui.foot = f !== 0 && f !== '0'; } catch (e) {}
    const arrived = takeHandoff();
    // приезд из соседнего тула со своими данными (/contract-review → пункты протокола)
    const extArrived = EXT && EXT.boot ? EXT.boot(api) : false;
    if (arrived || extArrived) started = true;
    if (window.HtmlPdf && window.HtmlPdf.installFonts) { try { window.HtmlPdf.installFonts(); } catch (e) {} }
    wire();
    paintWizard();
    setMode(ui.mode, false);
    buildForm();
    render();
    // склонение ФИО — правила грузятся отдельным файлом; до них лист показывает именительный
    if (window.FioEngine && !window.FioEngine.ready()) {
        window.FioEngine.init().then(() => { paintDerived(); render(); }).catch(err => {
            window.__handleError && window.__handleError(err, 'doc_builder_fio_rules', { say: tr('ui.err_fio') });
        });
    }
    drafts = initDrafts();
    if (drafts) drafts.boot();
    $('db').classList.add('is-ready');
    stage('ready', { sc: String(wizardValue(PAGE, S)) });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
